/**
 * PayPal-Ratenzahlung (Pay Later) Eligibility Logic — Frontend mirror of
 * /app/backend/paypal_eligibility.py
 *
 * Business rules:
 *   - Only products that are immediately deliverable (stockQuantity > 0)
 *     are eligible. Preorder items are NEVER eligible.
 *   - Only specific machine slugs / categories / all accessories qualify.
 *   - Hard cap of 10 000 € on the FINANCED gross total.
 *   - Surcharge = 10 % on the BRUTTO (gross) price, treated as a non-VAT
 *     pass-through fee. The customer pays brutto + 10 % at PayPal.
 */

// Whitelisted Bagger slugs
export const ELIGIBLE_PRODUCT_SLUGS = new Set([
  'mini-bagger-qw10',
  'mini-bagger-qw12',
  'mini-bagger-qw12s',
]);

// Entire categories that are eligible (case-sensitive, must match product.category)
export const ELIGIBLE_CATEGORIES = new Set([
  'Rüttelplatte',
  'Stampfer',
]);

// All accessories are eligible (subject to stock)
export const ACCESSORIES_ELIGIBLE = true;

// Hard upper limit (EUR, gross/brutto) — PayPal rule
export const PAYPAL_MAX_AMOUNT_EUR = 10_000.00;

// 10 % surcharge on GROSS price (Verkäuferfinanzierte 0%-Finanzierung)
export const PAYPAL_GROSS_SURCHARGE = 0.10;
// Legacy alias — kept for any old code that still imports this name.
// Numerically the same effect since net × 1.19 × 1.10 == net × 1.10 × 1.19.
export const PAYPAL_NET_SURCHARGE = PAYPAL_GROSS_SURCHARGE;

export const VAT_RATE = 0.19;
export const PAYPAL_INSTALLMENT_MONTHS = 24;

/**
 * Helper: does the product have stock right now?
 * Treats `stockQuantity > 0` OR `availability === 'in_stock'` as in-stock.
 * Falls back to "in-stock" when no stock field is present (so admin-edited
 * products without a stockQuantity field aren't accidentally excluded).
 */
export function isInStock(item) {
  if (!item) return false;
  if (typeof item.stockQuantity === 'number') return item.stockQuantity > 0;
  if (item.availability === 'in_stock') return true;
  if (item.availability === 'preorder' || item.availability === 'out_of_stock') return false;
  // Unknown → assume in-stock (most legacy items)
  return true;
}

/**
 * Is a single product/accessory eligible for PayPal-Ratenzahlung?
 * @param {{slug?: string, category?: string, isAccessory?: boolean, stockQuantity?: number, availability?: string}} item
 */
export function isProductEligible(item = {}) {
  // 1. Must be in stock (no preorders)
  if (!isInStock(item)) return false;

  // 2. Must be on the eligibility list
  if (item.isAccessory && ACCESSORIES_ELIGIBLE) return true;
  if (item.slug && ELIGIBLE_PRODUCT_SLUGS.has(item.slug)) return true;
  if (item.category && ELIGIBLE_CATEGORIES.has(item.category)) return true;
  return false;
}

/**
 * Compute the PayPal-Ratenzahlung price breakdown.
 *
 * NEW model (matches backend order_routes.py): the surcharge is 10 % of the
 * GROSS (brutto) price and is NOT subject to VAT itself. So:
 *   grossOriginal  = net × 1,19
 *   surchargeGross = grossOriginal × 0,10
 *   grossFinanced  = grossOriginal + surchargeGross  (what PayPal sees)
 *
 * Returns keys compatible with the previous shape so existing callers
 * keep working: { netStandard, netFinanced, vatAmount, grossFinanced,
 * surchargeGross, grossOriginal, surchargePct, vatPct }.
 */
export function financingPriceFromNet(netEur) {
  const net = Math.round((Number(netEur) || 0) * 100) / 100;
  const grossOriginal = Math.round(net * (1 + VAT_RATE) * 100) / 100;
  const surchargeGross = Math.round(grossOriginal * PAYPAL_GROSS_SURCHARGE * 100) / 100;
  const grossFinanced = Math.round((grossOriginal + surchargeGross) * 100) / 100;
  return {
    netStandard: net,
    grossOriginal,
    surchargeGross,
    grossFinanced,
    // Legacy fields — kept for back-compat in older components
    netFinanced: net,                 // net product price (unchanged)
    vatAmount: Math.round(net * VAT_RATE * 100) / 100,
    surchargePct: Math.round(PAYPAL_GROSS_SURCHARGE * 100),
    vatPct: Math.round(VAT_RATE * 100),
  };
}

/**
 * Compute breakdown from a GROSS price.
 */
export function financingPriceFromGross(grossEur) {
  const gross = Number(grossEur) || 0;
  const net = gross / (1 + VAT_RATE);
  return financingPriceFromNet(net);
}

/**
 * Convenience: monthly rate for a given net price (24-month default).
 * Returns null if the product can't be financed via PayPal-Raten.
 */
export function monthlyRateFromNet(netEur, months = PAYPAL_INSTALLMENT_MONTHS) {
  const fin = financingPriceFromNet(netEur);
  if (!fin.grossFinanced) return null;
  if (fin.grossFinanced > PAYPAL_MAX_AMOUNT_EUR + 0.005) return null;
  return Math.round(fin.grossFinanced / months * 100) / 100;
}

/**
 * Decide if an entire cart qualifies for PayPal-Ratenzahlung.
 * @param {Array} items
 * @param {number} totalGrossEur  brutto total INCLUDING the 10% surcharge
 * @returns {{eligible: boolean, reason: string|null, maxAmountEur: number}}
 */
export function cartEligible(items, totalGrossEur) {
  if (!items || items.length === 0) {
    return { eligible: false, reason: 'Warenkorb ist leer.', maxAmountEur: PAYPAL_MAX_AMOUNT_EUR };
  }

  const nonEligible = items.filter((it) => !isProductEligible({
    slug: it.slug,
    category: it.category,
    isAccessory: !!it.isAccessory,
    stockQuantity: it.stockQuantity,
    availability: it.availability,
  }));
  if (nonEligible.length > 0) {
    const names = nonEligible.map((it) => it.name || it.slug || 'Artikel').join(', ');
    return {
      eligible: false,
      reason: `Nicht für PayPal-Ratenzahlung freigegeben: ${names}`,
      maxAmountEur: PAYPAL_MAX_AMOUNT_EUR,
    };
  }

  if (Number(totalGrossEur) > PAYPAL_MAX_AMOUNT_EUR + 0.005) {
    return {
      eligible: false,
      reason: `PayPal-Ratenzahlung ist nur bis zu einem Bruttobetrag von ${PAYPAL_MAX_AMOUNT_EUR.toLocaleString('de-DE')} € möglich.`,
      maxAmountEur: PAYPAL_MAX_AMOUNT_EUR,
    };
  }

  return { eligible: true, reason: null, maxAmountEur: PAYPAL_MAX_AMOUNT_EUR };
}

export const PAYPAL_INSTALLMENT_DISCLAIMER =
  'PayPal-Ratenzahlung nur für Privatkunden mit Privat-PayPal-Konto (keine PayPal-Geschäftskonten). Vorbehaltlich Prüfung und Genehmigung durch PayPal. Verfügbar nur für berechtigte Kunden und sofort lieferbare Produkte bis 10.000 €. Die Finanzierung erfolgt zinsfrei (0 %) für den Käufer – die Finanzierungskosten werden von Jannesmann Baumaschinen übernommen. Die tatsächlichen Raten und Laufzeiten werden im PayPal-Checkout angezeigt.';

export const PAYPAL_HOME_BANNER_TEXT =
  'Jetzt auch 0 % PayPal-Ratenzahlung möglich – für sofort lieferbare Bagger, Rüttelplatten, Stampfer und Zubehör.';

export const PAYPAL_PRIVATE_ONLY_HINT =
  'Nur für Privatkunden mit Privat-PayPal-Konto verfügbar (nicht für PayPal-Geschäftskonten).';
